'use client';
import { type MouseEvent, type ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { Backdrop } from '@/app/components/overlays/backdrop';
import styles from '@/app/components/shared/dealer/styles.module.css';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';

interface BaseModalProps {
  className?: string;
  contentClassName?: string;
  isOpen: boolean;
  isFullScreen?: boolean;
  children: ReactNode;
  onClose: () => void;
}

export const BaseDealerModal = ({
  className,
  contentClassName,
  isOpen = false,
  isFullScreen = true,
  onClose,
  children,
}: BaseModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClosePopup = (
    event: MouseEvent<HTMLButtonElement | HTMLDivElement>
  ) => {
    event.stopPropagation();
    onClose();
  };

  const fullScreenClassName = cn(
    'xl:pointer-events-none xl:flex-col xl:justify-center',
    styles.baseModalFullScreen
  );

  const defaultClassName = cn(
    'pointer-events-none flex-col items-center justify-center p-4 xl:p-0',
    styles.baseModal
  );

  if (!isOpen) return null;

  return createPortal(
    <>
      <Backdrop
        className={cn('pointer-events-auto z-50', {
          'hidden xl:block': isFullScreen,
        })}
        onClick={handleClosePopup}
      />
      <div
        tabIndex={-1}
        ref={modalRef}
        className={cn(
          'fixed inset-0 z-50 mx-auto flex w-full overflow-y-auto',
          {
            [defaultClassName]: !isFullScreen,
            [fullScreenClassName]: isFullScreen,
          },
          className
        )}
        {...createDataAttributes({
          test: 'modal',
        })}
      >
        <div
          className={cn(
            'pointer-events-auto relative z-50 w-full bg-white',
            {
              'h-full overflow-hidden border-t border-t-gray-50 xl:h-9/12 xl:rounded-xs':
                isFullScreen,
              'h-fit rounded-lg': !isFullScreen,
            },
            contentClassName
          )}
        >
          {children}
        </div>
      </div>
    </>,
    document.body
  );
};
